import React from 'react';
import { Avatar } from './avatar';

// Color palette based on UID hash (consistent with AssignedUsersBubbles)
const AVATAR_COLORS = [
  'bg-azul-surelia/20 text-azul-surelia',
  'bg-verde-lima/30 text-azul-surelia',
  'bg-cian-digital/20 text-cian-digital',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
  'bg-indigo-100 text-indigo-700',
];

const getAvatarColor = (uid) => {
  if (!uid) return AVATAR_COLORS[0];
  const hash = uid.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

/**
 * BitacoraEntry Component
 * Displays a single bitácora log entry
 *
 * @param {Object} entry - The bitácora log entry
 * @param {string} entry.userName - Name of the user who performed the action
 * @param {string} entry.userEmail - Email of the user
 * @param {number} entry.timestamp - Unix timestamp
 * @param {string} entry.action - Action type (CREATE, UPDATE, DELETE)
 * @param {string} entry.metadata.description - Human-readable description
 * @param {Array} entry.changes - Optional array of changes
 */
export function BitacoraEntry({ entry }) {
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionBadge = (action) => {
    const badges = {
      CREATE: { color: 'bg-green-100 text-green-800', label: 'Creó' },
      UPDATE: { color: 'bg-blue-100 text-blue-800', label: 'Actualizó' },
      DELETE: { color: 'bg-red-100 text-red-800', label: 'Eliminó' },
    };
    return badges[action] || { color: 'bg-gray-100 text-gray-800', label: action };
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  };

  const formatDescription = (description) => {
    if (!description) return 'Sin descripción';

    // Split by keywords and style them
    const parts = description.split(/(Asignó|Desasignó)/g);

    return parts.map((part, index) => {
      if (part === 'Asignó') {
        return <span key={index} className="text-green-600 font-medium">{part}</span>;
      }
      if (part === 'Desasignó') {
        return <span key={index} className="text-red-600 font-medium">{part}</span>;
      }
      return part;
    });
  };

  const badge = getActionBadge(entry.action);
  const initials = getInitials(entry.userName || entry.userEmail);
  const avatarColor = getAvatarColor(entry.userId);

  return (
    <div className="flex gap-3 py-3">
      {/* Avatar */}
      <div className="flex-shrink-0">
        <Avatar
          initials={initials}
          className={`size-8 ${avatarColor}`}
          alt={entry.userName || entry.userEmail || 'Usuario'}
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            {/* User name and action */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900 text-sm">
                {entry.userName || entry.userEmail || 'Usuario desconocido'}
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>
                {badge.label}
              </span>
            </div>

            {/* Description */}
            <p className="text-sm text-gray-700 mt-1">
              {formatDescription(entry.metadata?.description)}
            </p>

            {/* Client/Convenio name and timestamp */}
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
              {entry.metadata?.convenioName ? (
                <>
                  Convenio: <span className="text-gray-400">
                    {entry.metadata.convenioName}
                    {entry.metadata?.beneficiarioNombre ? ` — ${entry.metadata.beneficiarioNombre}` : ''}
                  </span>
                  <span className="text-gray-300">•</span>
                </>
              ) : entry.metadata?.clientName ? (
                <>
                  Cliente: <span className="text-gray-400">{entry.metadata.clientName}</span>
                  <span className="text-gray-300">•</span>
                </>
              ) : null}
              <span>{formatTimestamp(entry.timestamp)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BitacoraEntry;
